import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, LogOut, Mail, Pencil, Plus, Trash2, UserCheck, UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormError, PageHeader } from "@/components/common/ui-bits";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

function AddUserDialog({ open, onClose, onAdded }) {
  const [form, setForm] = useState({ name: "", email: "", role: "standard", password: "", include_password_in_email: false });
  const [error, setError] = useState("");
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setError(""); };
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/users", form);
      if (data.welcome_email === "sent") toast.success(`User created. Welcome email sent to ${data.email}`);
      else toast.warning("User created, but the welcome email couldn't be sent. Use Resend welcome email.");
      setForm({ name: "", email: "", role: "standard", password: "", include_password_in_email: false });
      onAdded();
    } catch (err) { setError(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card rounded-2xl" data-testid="add-user-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">Add user</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div><Label htmlFor="u-name">Name</Label><Input id="u-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="add-user-name-input" /></div>
          <div><Label htmlFor="u-email">Email</Label><Input id="u-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="add-user-email-input" /></div>
          <div><Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="add-user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard" data-testid="add-user-role-standard">Standard (add entries only)</SelectItem>
                <SelectItem value="admin" data-testid="add-user-role-admin">Administrator (full access)</SelectItem>
              </SelectContent>
            </Select></div>
          <div><Label htmlFor="u-pw">Temporary password (min 8 characters)</Label><Input id="u-pw" type="text" value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5 h-11 rounded-xl font-num" data-testid="add-user-password-input" /></div>
          <label className="flex items-start gap-3 text-sm cursor-pointer" htmlFor="u-incl">
            <Checkbox id="u-incl" checked={form.include_password_in_email} onCheckedChange={(v) => set("include_password_in_email", v === true)} className="mt-0.5 h-5 w-5" data-testid="add-user-include-password-checkbox" />
            <span>Include temporary password in email<span className="block text-xs text-muted-foreground">A welcome email with the sign-in link is always sent.</span></span>
          </label>
          <FormError message={error} testId="add-user-error" />
          <Button type="submit" className="w-full h-11 rounded-xl" data-testid="add-user-submit-button">Create user</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetDialog({ target, onClose }) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  if (!target) return null;
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/users/${target.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${target.name}`);
      setPw(""); onClose();
    } catch (err) { setError(errMsg(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card rounded-2xl" data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">Reset password · {target.name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div><Label htmlFor="r-pw">New temporary password</Label><Input id="r-pw" value={pw} onChange={(e) => { setPw(e.target.value); setError(""); }} className="mt-1.5 h-11 rounded-xl font-num" data-testid="reset-password-input" /></div>
          <FormError message={error} testId="reset-password-error" />
          <Button type="submit" className="w-full h-11 rounded-xl" data-testid="reset-password-submit-button">Reset password</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ target, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [error, setError] = useState("");
  useEffect(() => { if (target) { setForm({ name: target.name, email: target.email }); setError(""); } }, [target]);
  if (!target) return null;
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch(`/users/${target.id}`, form);
      toast.success("User updated");
      onSaved(data);
    } catch (err) { setError(errMsg(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card rounded-2xl" data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">Edit user</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div><Label htmlFor="e-name">Name</Label><Input id="e-name" value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); setError(""); }} className="mt-1.5 h-11 rounded-xl" data-testid="edit-user-name-input" /></div>
          <div><Label htmlFor="e-email">Email (used to sign in and for alert emails)</Label><Input id="e-email" type="email" value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); setError(""); }} className="mt-1.5 h-11 rounded-xl" data-testid="edit-user-email-input" /></div>
          <FormError message={error} testId="edit-user-error" />
          <Button type="submit" className="w-full h-11 rounded-xl" data-testid="edit-user-submit-button">Save changes</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteUserDialog({ target, onClose, onDeleted }) {
  const [typed, setTyped] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTyped(""); setError(""); }, [target]);
  if (!target) return null;
  const matches = typed.trim().toLowerCase() === target.email.toLowerCase();
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${target.id}/delete`, { confirm_email: typed.trim() });
      toast.success(data.message);
      onDeleted();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card rounded-2xl" data-testid="delete-user-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">Delete {target.name}?</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-sm text-muted-foreground" data-testid="delete-user-warning">
            This permanently removes the account and signs them out everywhere. Their entries, bills and activity stay, shown as "{target.name} (deleted user)". To only pause access, use Deactivate instead.
          </p>
          <div><Label htmlFor="d-email">Type <span className="font-semibold text-foreground">{target.email}</span> to confirm</Label>
            <Input id="d-email" value={typed} onChange={(e) => { setTyped(e.target.value); setError(""); }} autoComplete="off" className="mt-1.5 h-11 rounded-xl" data-testid="delete-user-confirm-input" /></div>
          <FormError message={error} testId="delete-user-error" />
          <Button type="submit" disabled={!matches || busy} className="w-full h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white" data-testid="delete-user-submit-button">Delete user permanently</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserRow({ u, isMe, onPatch, onReset, onEdit, onDelete }) {
  const key = u.email.replace(/[^a-z0-9]+/gi, "-");
  return (
    <li className="flex flex-wrap items-center gap-3 px-4 sm:px-5 py-4" data-testid={`user-row-${key}`}>
      <div className="h-10 w-10 shrink-0 rounded-full bg-accent text-accent-foreground grid place-items-center font-heading font-bold text-sm">
        {u.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
      </div>
      <div className="min-w-0 flex-1 basis-48">
        <div className="text-sm font-semibold truncate">{u.name}{isMe && <span className="ml-1.5 text-xs text-muted-foreground font-normal">(you)</span>}</div>
        <div className="text-xs text-muted-foreground truncate">{u.email}</div>
      </div>
      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${u.is_active ? "bg-emerald-50 text-emerald-800" : "bg-secondary text-muted-foreground"}`} data-testid={`user-status-${key}`}>
        {u.is_active ? "Active" : "Disabled"}
      </span>
      <Select value={u.role} onValueChange={(v) => onPatch(u, { role: v })}>
        <SelectTrigger className="h-9 w-[150px] rounded-lg" data-testid={`user-role-select-${key}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="admin" data-testid={`user-role-admin-${key}`}>Administrator</SelectItem>
          <SelectItem value="standard" data-testid={`user-role-standard-${key}`}>Standard</SelectItem>
        </SelectContent>
      </Select>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="h-9 rounded-lg" onClick={() => onEdit(u)} data-testid={`user-edit-${key}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
        <Button size="sm" variant="outline" className="h-9 rounded-lg" data-testid={`user-sign-out-everywhere-${key}`}
          onClick={async () => { const { data } = await api.post(`/users/${u.id}/sign-out-everywhere`); toast.success(`${u.name} signed out on ${data.signed_out} ${data.signed_out === 1 ? "device" : "devices"}`); }}>
          <LogOut className="h-3.5 w-3.5 mr-1" />Sign out everywhere</Button>
        <Button size="sm" variant="outline" className="h-9 rounded-lg" data-testid={`user-resend-welcome-${key}`}
          onClick={async () => { try { const { data } = await api.post(`/users/${u.id}/welcome-email`); toast.success(data.message); } catch (e) { toast.error(errMsg(e)); } }}>
          <Mail className="h-3.5 w-3.5 mr-1" />Resend welcome email</Button>
        <Button size="sm" variant="outline" className="h-9 rounded-lg" onClick={() => onReset(u)} data-testid={`user-reset-password-${key}`}><KeyRound className="h-3.5 w-3.5 mr-1" />Reset</Button>
        {u.is_active ? (
          <Button size="sm" variant="outline" className="h-9 rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => onPatch(u, { is_active: false })} data-testid={`user-deactivate-${key}`}><UserX className="h-3.5 w-3.5 mr-1" />Deactivate</Button>
        ) : (
          <Button size="sm" variant="outline" className="h-9 rounded-lg" onClick={() => onPatch(u, { is_active: true })} data-testid={`user-activate-${key}`}><UserCheck className="h-3.5 w-3.5 mr-1" />Activate</Button>
        )}
        {!isMe && (
          <Button size="sm" variant="outline" className="h-9 rounded-lg text-rose-700 border-rose-300 hover:bg-rose-50" onClick={() => onDelete(u)} data-testid={`user-delete-${key}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Delete user</Button>
        )}
      </div>
    </li>
  );
}

export default function UsersPage() {
  const { user: me, setUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const patch = async (u, body) => {
    setError("");
    try {
      await api.patch(`/users/${u.id}`, body);
      toast.success("User updated");
      load();
    } catch (e) { setError(errMsg(e)); toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-5xl">
      <PageHeader eyebrow="Who can sign in" title="Users" subtitle="There is no public sign-up. Create accounts here. Each new user gets a welcome email with the sign-in link." testId="users-title">
        <Button onClick={() => setAdding(true)} className="rounded-xl h-10" data-testid="add-user-button"><Plus className="h-4 w-4 mr-1" />Add user</Button>
      </PageHeader>
      {error && <div className="mb-4"><FormError message={error} testId="users-page-error" /></div>}
      <ul className="surface divide-y divide-border overflow-hidden fade-up" data-testid="users-list">
        {users.map((u) => <UserRow key={u.id} u={u} isMe={u.id === me.id} onPatch={patch} onReset={setResetting} onEdit={setEditing} onDelete={setDeleting} />)}
      </ul>
      <DeleteUserDialog target={deleting} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); load(); }} />
      <EditUserDialog target={editing} onClose={() => setEditing(null)} onSaved={(d) => { if (d.id === me.id) setUser(d); setEditing(null); load(); }} />
      <AddUserDialog open={adding} onClose={() => setAdding(false)} onAdded={() => { setAdding(false); load(); }} />
      <ResetDialog target={resetting} onClose={() => setResetting(null)} />
    </div>
  );
}
